import * as React from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { AccountForm, type AsyncFlagEvaluator } from '@opendoor/auth-fe';
import { Box } from '@opendoor/bricks-next';
import { analytics, globalObservability } from '@opendoor/observability/slim';
import { BricksNextLoader as Loader } from 'components/shared/BricksNextLoader';
import Cookies from 'js-cookie';

import { getLoggedInRedirectUrl } from '../../helpers/accountLoggedInRedirect';
import {
  clearMagicLinkAccountSession,
  isMagicLinkAccountSession,
} from '../../helpers/magicLinkAccountSession';
import * as api from '../api';
import LogInConnector from '../shared/AccountConnector';
import type { AccountProps } from '../shared/AccountConnector';
import { AccountMagicLinkGate } from './AccountMagicLinkGate';

const Sentry = globalObservability.getSentryClient();

const UNAUTHORIZED_STATUS_CODE = 401;

interface IAccountFormPageProps extends AccountProps {
  config?: 'resaleClosingDashboard';
  redirectTo?: string | undefined;
  emailPrefill?: string | undefined;
  updatedPassword?: boolean;
}

const asyncEvaluateFlag: AsyncFlagEvaluator = async () => true;

const AccountFormPage: React.FC<IAccountFormPageProps> = ({
  logIn,
  register,
  requestHumanStatus,
  hasGeneratedPassword,
  isNewHuman = false,
  loginRequestStatus,
  humanStatusRequestStatus,
  registrationRequestStatus,
  config,
  email,
  emailPrefill,
  requestResetPassword,
  redirectTo,
  maybeFetchSessionInfo,
  loggedIn,
  serverLoginRedirect,
  updatedPassword,
  human,
  infoIsLoaded,
}) => {
  const getSignInText = (config?: string) => {
    let loginHeaderText: string | undefined;
    let registrationHeaderText: string | undefined;
    switch (config) {
      case 'resaleClosingDashboard':
        loginHeaderText = 'Sign in to get back to your dashboard';
        registrationHeaderText = 'Sign up to get access to your dashboard';
        break;

      default:
        break;
    }

    return { loginHeaderText, registrationHeaderText };
  };

  const { loginHeaderText, registrationHeaderText } = getSignInText(config);

  const redirectIfLoggedIn = useCallback(() => {
    if (!loggedIn || isMagicLinkAccountSession()) {
      return;
    }

    analytics.trackEvent('login', 'redirect', undefined, {
      email,
      config,
      redirectTo,
      serverLoginRedirect,
    });

    window.location.assign(getLoggedInRedirectUrl(redirectTo, serverLoginRedirect));
  }, [config, email, loggedIn, redirectTo, serverLoginRedirect]);

  const completeMagicLinkGate = useCallback(() => {
    clearMagicLinkAccountSession();
    redirectIfLoggedIn();
  }, [redirectIfLoggedIn]);

  const wasLoggedIn = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    analytics.register({ login: Cookies.get('login-test') || 'no-cookie' });
    analytics.trackEvent('login', 'load', undefined, {
      email,
      config,
      redirectTo,
      emailPrefill,
    });
    maybeFetchSessionInfo();
  }, []);

  useEffect(() => {
    if (wasLoggedIn.current === true && !loggedIn) {
      clearMagicLinkAccountSession();
    }
    wasLoggedIn.current = loggedIn;
    if (loggedIn) {
      redirectIfLoggedIn();
    }
  }, [loggedIn, loginRequestStatus, redirectIfLoggedIn]);

  useEffect(() => {
    if (
      loginRequestStatus &&
      loginRequestStatus.error &&
      loginRequestStatus.statusCode !== UNAUTHORIZED_STATUS_CODE
    ) {
      Sentry.captureException?.(loginRequestStatus.error);
    }
  }, [loginRequestStatus]);

  const trackLoginEvent = (
    topic: 'google-auth' | 'one-time-password',
    label: string,
    extra: Record<string, unknown>,
  ) => {
    analytics.trackEvent('login', topic, label, extra);
  };

  const handlePasswordlessLoginRequest = async (email: string) => {
    trackLoginEvent('one-time-password', 'request', { email, redirectTo });
    const response = await api.requestNewTokenByEmail(email, redirectTo);
    if (!response || !response.data || response.errors) {
      const errors = response && response.errors;
      trackLoginEvent('one-time-password', 'error', { email, errors });
      return { success: false, errors };
    }

    trackLoginEvent('one-time-password', 'success', { email });
    return { success: true };
  };

  let headerText = loginHeaderText;

  if (updatedPassword) {
    headerText = 'Your password was successfully changed. Please sign in.';
  }
  const onRegister = (
    email?: string,
    name?: string,
    password?: string,
    phone?: string,
    googleIDToken?: string,
  ) => {
    register(email, name, password, phone, googleIDToken);
    analytics.trackEvent('session', 'register', 'account-creation', { email });
  };

  if (loggedIn && isMagicLinkAccountSession()) {
    return (
      <Box
        py="$24x"
        px="$12x"
        mx="auto"
        maxWidth={480}
        width="100%"
        justifyContent="center"
        alignItems="center"
        $largerThanMD={{ py: '$36x', px: 0 }}
      >
        <AccountMagicLinkGate
          human={human}
          infoIsLoaded={infoIsLoaded}
          hasGeneratedPassword={hasGeneratedPassword}
          humanStatusRequestStatus={humanStatusRequestStatus}
          requestHumanStatus={requestHumanStatus}
          requestResetPassword={requestResetPassword}
          onComplete={completeMagicLinkGate}
        />
      </Box>
    );
  }

  return (
    <Box
      py="$24x"
      px="$12x"
      mx="auto"
      justifyContent="center"
      alignItems="center"
      $largerThanMD={{ py: '$36x', px: 0 }}
    >
      <AccountForm
        featureFlagProps={{
          evaluate: asyncEvaluateFlag,
          loading: <Loader size="large" backgroundColor="$backgroundPrimary" />,
        }}
        accountFormProps={{
          loginAnalyticsPrefix: 'cosmos-login',
          analyticsName: 'cosmos-account-form',
          loginHeaderText: headerText,
          registrationHeaderText: registrationHeaderText,
          email: email || emailPrefill,
          hasGeneratedPassword: hasGeneratedPassword,
          error:
            loginRequestStatus.error ||
            humanStatusRequestStatus.error ||
            registrationRequestStatus.error,
          passwordLoading:
            !!serverLoginRedirect ||
            loginRequestStatus.isLoading ||
            registrationRequestStatus.isLoading,
          emailLoading: humanStatusRequestStatus.isLoading,
          isNewHuman: isNewHuman,
          onLogin: logIn,
          overrideHasSubmittedEmailCheckForLogin: !!emailPrefill,
          onRegister: onRegister,
          onHumanStatusRequest: requestHumanStatus,
          requestResetPassword,
          onPasswordlessLoginRequest: (email: string) => handlePasswordlessLoginRequest(email),
          showGoogleAuth: true,
          loginRedirectTo: redirectTo,
        }}
      />
    </Box>
  );
};

export default LogInConnector(AccountFormPage);
